import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { settingsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

export const EditUserDialog = ({ user, onClose, onSave }) => {
  const { user: currentUser } = useAuth();
  const [formData, setFormData] = useState({
    nom: user.nom || '',
    email: user.email || user.username || '',
    telephone: user.telephone || '',
    pays: user.pays || '',
    credits_ia: user.credits_ia || 0,
    type_pack: user.type_pack || 'Gratuit',
    debut_abonnement: user.debut_abonnement ? new Date(user.debut_abonnement).toISOString().split('T')[0] : '',
    fin_abonnement: user.fin_abonnement ? new Date(user.fin_abonnement).toISOString().split('T')[0] : ''
  });

  const [packs, setPacks] = useState([]);
  const [loadingPacks, setLoadingPacks] = useState(true);

  useEffect(() => {
    const loadPacks = async () => {
      try {
        let availablePacks = [];
        // Target center ID: either the user's center, or the current admin's ID
        const targetCenterId = user.center_id || (currentUser?.role === 'admin' ? currentUser.id : null);
        
        if (targetCenterId) {
          availablePacks = await settingsAPI.getCenterPacks(targetCenterId);
        } else {
          availablePacks = await settingsAPI.getPacks();
        }
        setPacks(availablePacks || []);
      } catch (error) {
        console.error('Error loading packs for dialog:', error);
      } finally {
        setLoadingPacks(false);
      }
    };
    loadPacks();
  }, [user.center_id, user.id, currentUser?.id]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(user.id, {
      ...formData,
      credits_ia: parseInt(formData.credits_ia)
    });
  };

  const handlePackChange = (packName) => {
    const selected = packs.find(p => p.name === packName);
    if (!selected) {
      setFormData(f => ({ ...f, type_pack: packName }));
      return;
    }
    const credits = selected.essais_ia ?? selected.essais_count ?? 0;
    const today = new Date();
    let fin = '';
    if (selected.duration) {
      const d = selected.duration.toLowerCase();
      const copy = new Date(today);
      const num = parseInt(d);
      if (!isNaN(num)) {
        if (d.includes('an') || d.includes('year')) copy.setFullYear(copy.getFullYear() + num);
        else if (d.includes('semaine') || d.includes('week')) copy.setDate(copy.getDate() + num * 7);
        else copy.setMonth(copy.getMonth() + num); // default: months
        fin = copy.toISOString().split('T')[0];
      }
    }
    setFormData(f => ({
      ...f,
      type_pack: packName,
      credits_ia: credits > 0 ? credits : f.credits_ia,
      debut_abonnement: today.toISOString().split('T')[0],
      ...(fin ? { fin_abonnement: fin } : {})
    }));
  };

  const packTypes = packs.length > 0 ? packs.map(p => p.name) : ['Gratuit', 'Basique', 'Standard', 'Premium', 'VIP'];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-card rounded-xl border shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="edit-user-dialog"
      >
        <div className="sticky top-0 bg-card border-b p-6 flex items-center justify-between">
          <h2 className="text-2xl font-semibold">Modifier l'utilisateur</h2>
          <button
            onClick={onClose}
            data-testid="close-dialog"
            className="p-2 rounded-lg hover:bg-secondary transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Nom complet</label>
              <input
                type="text"
                value={formData.nom}
                onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                data-testid="edit-nom-input"
                className="w-full h-10 px-3 rounded-md border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                placeholder="Nom de l'utilisateur"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                data-testid="edit-email-input"
                className="w-full h-10 px-3 rounded-md border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                placeholder="email@exemple.com"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Téléphone</label>
              <input
                type="tel"
                value={formData.telephone}
                onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                data-testid="edit-telephone-input"
                className="w-full h-10 px-3 rounded-md border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                placeholder="+1-XXX-XXX-XXXX"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Pays</label>
              <input
                type="text"
                value={formData.pays}
                onChange={(e) => setFormData({ ...formData, pays: e.target.value })}
                data-testid="edit-pays-input"
                className="w-full h-10 px-3 rounded-md border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                placeholder="Canada"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Essais IA</label>
              <input
                type="number"
                value={formData.credits_ia}
                onChange={(e) => setFormData({ ...formData, credits_ia: e.target.value })}
                data-testid="edit-credits-input"
                className="w-full h-10 px-3 rounded-md border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                min="0"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Type de pack</label>
              <select
                value={formData.type_pack}
                onChange={(e) => handlePackChange(e.target.value)}
                data-testid="edit-pack-select"
                className="w-full h-10 px-3 rounded-md border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                {packTypes.map((pack) => (
                  <option key={pack} value={pack}>{pack}</option>
                ))}
              </select>
              {(() => {
                const sel = packs.find(p => p.name === formData.type_pack);
                const credits = sel ? (sel.essais_ia ?? sel.essais_count ?? 0) : 0;
                return sel && credits > 0 ? (
                  <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1 ml-1">
                    ✓ {credits} essais IA inclus dans ce pack
                  </p>
                ) : null;
              })()}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Début d'abonnement</label>
              <input
                type="date"
                value={formData.debut_abonnement}
                onChange={(e) => setFormData({ ...formData, debut_abonnement: e.target.value })}
                data-testid="edit-debut-input"
                className="w-full h-10 px-3 rounded-md border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Fin d'abonnement</label>
              <input
                type="date"
                value={formData.fin_abonnement}
                onChange={(e) => setFormData({ ...formData, fin_abonnement: e.target.value })}
                data-testid="edit-fin-input"
                className="w-full h-10 px-3 rounded-md border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-3 justify-end pt-4">
            <button
              type="button"
              onClick={onClose}
              data-testid="cancel-edit-button"
              className="px-4 py-2 rounded-lg border hover:bg-secondary transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              data-testid="save-edit-button"
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              Enregistrer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
